import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ThumbsUp, ThumbsDown, Trophy, Play, Loader2, Users, HelpCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { AnvilCockpit } from "@/components/deal/AnvilCockpit";
import { PreviewTabs } from "@/components/deal/PreviewTabs";
import { DocumentsCard, CrewPayroll, LaborBreakout } from "@/components/deal/DealExtras";
import { SendReadinessBanner } from "@/components/deal/SendReadinessBanner";
import { PackageTruth } from "@/components/deal/PackageTruth";import { AcquisitionPathChip } from "@/components/deal/AcquisitionPathChip";
import { FableVerify } from "@/components/deal/FableVerify";
import { WageRegimeChip } from "@/components/deal/WageRegimeChip";
import { WageDeterminationChip } from "@/components/deal/WageDeterminationChip";
import { PricingReadinessPanel } from "@/components/deal/PricingReadinessPanel";
import { PricingGroundingCard } from "@/components/deal/PricingGroundingCard";
import { AmendmentImpactChip } from "@/components/deal/AmendmentImpactChip";
import { AmendmentProgressBanner } from "@/components/deal/AmendmentProgressBanner";
import { MissingDrawingsSpecsBanner } from "@/components/deal/MissingDrawingsSpecsBanner";
import { ManualFormsPanel } from "@/components/deal/ManualFormsPanel";
import { IntakeFormsPanel } from "@/components/deal/IntakeFormsPanel";
import { WageActConfirm } from "@/components/deal/WageActConfirm";
import KeyPersonnelModal from "@/components/deal/KeyPersonnelModal";
import { BidCapacityCard } from "@/components/deal/BidCapacityCard";
import { ResetControl } from "@/components/deal/ResetControl";
import { SourceDocGate } from "@/components/deal/SourceDocGate";
import { NonBiddableGate } from "@/components/deal/NonBiddableGate";
import { Stage1Brief } from "@/components/deal/Stage1Brief";

const Meta = ({ label, v }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</div><div className="text-zinc-900 truncate">{v}</div></div>
);

// Data-source breakdown (Dir 170j) — the engine produces the price and stands behind its number. We show
// WHERE the priced dollars come from (published / measured / comp) plus any factual review advisories.
// No confidence score, no "firm/not-firm" hedging, no confidence-withholding.
const ConfidenceBadge = ({ ec }) => {
  if (!ec || !ec.available) return null;
  const pub = ec.published_pct || 0;
  const meas = ec.measured_pct || 0;
  const comp = (ec.comp_pct || 0) + (ec.estimated_pct || 0) + (ec.flagged_pct || 0);
  const advisories = ec.advisories || [];
  return (
    <div className="mt-2 flex flex-col items-end gap-1" data-testid="estimate-confidence">
      <div className="text-[10px] uppercase tracking-wider text-zinc-400">Data sources</div>
      <div className="flex h-1.5 w-40 rounded-full overflow-hidden bg-zinc-100" data-testid="estimate-data-source-bar"
        title="Share of priced dollars by data source: published (bid schedule / M&P), measured (geometry / SDK), comp (rate benchmark)">
        <div className="bg-emerald-500" style={{ width: `${pub}%` }} />
        <div className="bg-sky-500" style={{ width: `${meas}%` }} />
        <div className="bg-amber-400" style={{ width: `${comp}%` }} />
      </div>
      <div className="text-[10px] text-zinc-500" data-testid="estimate-data-sources">
        {pub.toFixed(0)}% published · {meas.toFixed(0)}% measured · {comp.toFixed(0)}% comp
      </div>
      <div className="text-[10px] text-zinc-500" data-testid="estimate-total">
        {ec.scored_basis && ec.scored_basis !== ec.total
          ? <span className="text-zinc-400">Scored basis (w/ options): ${ec.scored_basis.toLocaleString()}</span>
          : null}
      </div>
      {advisories.map((a, i) => (
        <div key={i} className="text-[10px] text-amber-700 max-w-[240px] text-right" data-testid="estimate-advisory">
          {a}
        </div>
      ))}
    </div>
  );
};


export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [users, setUsers] = useState([]);
  const [startSignal, setStartSignal] = useState(0);
  const [showKP, setShowKP] = useState(false);

  const load = () => api.get(`/bids/${id}`).then((r) => setDeal(r.data)).catch(() => navigate("/pipeline"));
  useEffect(() => {
    load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    const onRefresh = () => load();
    window.addEventListener("ironclad-refresh", onRefresh);
    return () => window.removeEventListener("ironclad-refresh", onRefresh);
    /* eslint-disable-next-line */
  }, [id]);

  // P0 (2026-07-13): poll the TRUE engine state on any ACTIVE pursuit — NOT only when the UI already thinks
  // it's 'running'. This closes the observability gap where a run kicked off server-side / via the API (not the
  // UI button) flips to 'running' AFTER page load: the poll now detects that transition and reflects it locally,
  // which fires AnvilCockpit's re-attach effect so the live SSE stream opens and the step timeline rebuilds from
  // the backend snapshot (instead of the page looking frozen). A stale 'running' spinner (crashed/redeployed
  // mid-run) is still corrected to error/complete server-side.
  useEffect(() => {
    if (!deal) return;
    const active = deal.pipeline_stage !== "Won" && deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed";
    if (!active) return;
    const t = setInterval(() => {
      api.get(`/bids/${id}/engine/status`).then((r) => {
        const s = r.data || {};
        if (!s.engine_status) return;
        if (s.engine_status === "running") {
          // Transition INTO running (server-side / API trigger) — reflect it so the cockpit attaches the stream.
          setDeal((d) => (d && d.engine_status !== "running" ? { ...d, engine_status: "running", engine_error: s.engine_error } : d));
        } else if (s.engine_status !== deal.engine_status) {
          // Terminal/idle state differs from what we're showing → full reload to refresh stages + preview.
          load();
        }
      }).catch(() => {});
    }, 12000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [deal?.engine_status, deal?.pipeline_stage, id]);

  if (!deal) return <div className="p-8 text-zinc-500 font-mono text-sm animate-pulse">Loading deal file…</div>;

  const isOfficer = !!user;  // Dir 65 — roles flattened: any authenticated employee has full access.
  const PIPE = ["Opportunity", "Pursuing", "Waiting", "Submitted"];
  const stage1Done = (deal.bid_stages || []).find((s) => s.number === "1")?.status === "COMPLETE";
  const isWon = deal.pipeline_stage === "Won";
  const proposalNo = deal.bid_type === "Commercial" ? deal.commercial_proposal_number : deal.solicitation_number;

  const decide = async (decision) => {
    const note = decision === "pass" ? (prompt("Reason for PASS (logged, not deleted):") || "") : "Match — pursue";
    if (decision === "pass" && !note) return;
    try {
      await api.post(`/bids/${id}/pursue`, { decision, note });
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "bid_fit_blocked") {
        const reasons = (det.reasons || []).join("\n\n");
        if (det.unset) {
          // FAIL-CLOSED: capacity not configured — must set real limits in the company profile first.
          window.alert("Stage-1 bid-fit gate (fail-closed):\n\n" + reasons +
            "\n\nConfigure the company's bonding limits and largest completed project in Company File before pursuing.");
        } else {
          // Exceeds a CONFIGURED limit — an officer may override with rationale.
          const ok = window.confirm("Stage-1 bid-fit gate:\n\n" + reasons +
            "\n\nThis exceeds WD's configured capacity. Override and pursue anyway (logged)?");
          if (ok) {
            await api.post(`/bids/${id}/pursue`, { decision, note: note + " [bid-fit override]", override_bid_fit: true });
            toast.success("Pursuing (bid-fit override logged) — Anvil is auto-running the bid.");
            setDeal((d) => (d ? { ...d, pipeline_stage: "Pursuing", status: "running", engine_status: "running" } : d));
            load();
            return;
          }
        }
        return;
      }
      toast.error(det?.message || det || "Pursue failed");
      return;
    }
    if (decision === "pursue") {
      // Directive 37 Part A — Pursue AUTO-ENGAGES ANVIL; reflect the engine running immediately + refetch.
      toast.success("Pursuing — Anvil is auto-running the bid. It will stop for your review before anything sends.");
      setDeal((d) => (d ? { ...d, pipeline_stage: "Pursuing", status: "running", engine_status: "running" } : d));
    } else {
      toast.success("Passed and logged");
    }
    load();
  };
  const movePipeline = async (stage) => {
    try { await api.post(`/bids/${id}/pipeline`, { pipeline_stage: stage }); toast.success(`Moved to ${stage}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const markWon = async () => {
    if (!window.confirm("Mark this deal WON? This creates a tracked PO, opens a project, and prepares the certified-payroll email.")) return;
    try {
      const r = await api.post(`/bids/${id}/won`);
      toast.success(`WON — PO ${r.data.po_code} created` + (r.data.missing?.length ? ` · ${r.data.missing.length} payroll field(s) MISSING` : ""));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark won"); }
  };
  const autoRun = () => { if (deal.engine_status !== "running") setStartSignal((n) => n + 1); };

  const requestHelp = async () => {
    const note = window.prompt("What do you need help with on this deal?");
    if (note === null) return;
    try { await api.post("/signals", { kind: "help", bid_id: id, note }); toast.success("Help requested — pinned to the Command feed."); }
    catch { toast.error("Couldn't post the help request."); }
  };
  const requestReview = async () => {
    const note = window.prompt("Ask a teammate to review this proposal (optional note):");
    if (note === null) return;
    try { await api.post("/signals", { kind: "review", bid_id: id, note }); toast.success("Review requested — pinned to the Command feed. This does not gate sending."); }
    catch { toast.error("Couldn't post the review request."); }
  };

  return (
    <div className="p-5 sm:p-8 max-w-[1700px] mx-auto" data-testid="deal-detail">
      <button onClick={() => navigate("/pipeline")} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft className="h-4 w-4" /> Pipeline
      </button>

      <div className="bg-white border border-zinc-200 shadow-sm rounded-xl p-5 mb-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${deal.bid_type === "Federal" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{deal.bid_type}</span>
              <span className="text-[11px] text-zinc-700 font-mono bg-zinc-100 px-2 py-0.5 rounded" data-testid="deal-proposal-no">Proposal #: {proposalNo || "—"}</span>
              <span className="text-[10px] text-zinc-400 font-mono" data-testid="deal-est-code">EST {deal.est_code}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full border border-gold-dark text-zinc-900">{deal.pipeline_stage}</span>
              {deal.engine_status && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  deal.engine_status === "complete" ? "bg-emerald-100 text-emerald-700"
                  : deal.engine_status === "running" ? "bg-amber-100 text-amber-700"
                  : deal.engine_status === "stopped" ? "bg-zinc-200 text-zinc-600" : "bg-red-100 text-red-600"}`} data-testid="deal-engine-status">
                  Anvil {deal.engine_status}
                </span>
              )}
            </div>
            <h1 className="font-display font-bold text-2xl text-zinc-900">{deal.title}</h1>
            <p className="text-sm text-zinc-500 mt-0.5">{deal.agency} · {deal.place_of_performance || "—"}</p>
            <div className="mt-2"><AmendmentImpactChip dealId={id} reload={load} /></div>
            {deal.llm_provider_tag && (
              <span
                data-testid="llm-bridge-badge"
                title="This bid was produced by a temporary non-production GPT-5 bridge, not the shipped Claude engine. For development/hardening only — not submission-ready."
                className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                {deal.llm_provider_tag} — dev run
              </span>
            )}
            {deal.bid_type === "Federal" && (
              <WageRegimeChip deal={deal} isOfficer={isOfficer} reload={load} />
            )}
            {deal.bid_type === "Federal" && (
              <WageDeterminationChip deal={deal} isOfficer={isOfficer} reload={load} />
            )}
            {deal.bid_type === "Federal" && (
              <AcquisitionPathChip deal={deal} isOfficer={isOfficer} reload={load} />
            )}
          </div>
          <div className="text-right">
            <div className="text-2xl font-display font-bold text-zinc-900" data-testid="deal-amount">{deal.estimate_confidence?.total_display || deal.amount || "$—"}</div>
            <div className="text-[11px] text-zinc-500">Reconciled total</div>
            <ConfidenceBadge ec={deal.estimate_confidence} />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
          <Meta label="Owner" v={deal.estimator || deal.owner_name} />
          <Meta label="Due" v={deal.due_date || "—"} />
          <Meta label={deal.bid_type === "Federal" ? "Solicitation" : "Site"} v={(deal.bid_type === "Federal" ? deal.solicitation_number : deal.site_code) || "—"} />
          <Meta label="Priority" v={deal.priority} />
        </div>

        {deal.bid_type === "Federal" && <WageActConfirm dealId={id} isOfficer={isOfficer} reload={load} />}
        <AmendmentProgressBanner dealId={id} reload={load} />
        <MissingDrawingsSpecsBanner items={deal.shred?.missing_drawings_specs} className="mt-4" />

        {deal.pipeline_stage === "Opportunity" ? (
          <>
            <BidCapacityCard bidId={id} />
            <div className="flex gap-3 mt-5 pt-4 border-t border-zinc-200" data-testid="pursue-pass">
              <button data-testid="pursue-btn" onClick={() => decide("pursue")} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2.5 rounded-md"><ThumbsUp className="h-4 w-4" /> Pursue</button>
              <button data-testid="pass-btn" onClick={() => decide("pass")} className="flex items-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-sm font-semibold px-4 py-2.5 rounded-md"><ThumbsDown className="h-4 w-4" /> Pass</button>
            </div>
          </>
        ) : deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed" ? (
          <div className="flex items-center gap-3 mt-5 pt-4 border-t border-zinc-200 flex-wrap" data-testid="deal-actions">
            <label className="text-xs text-zinc-500">Pipeline
              <select data-testid="pipeline-select" value={deal.pipeline_stage} onChange={(e) => movePipeline(e.target.value)} className="ml-2 bg-white border border-zinc-300 rounded-md px-2 py-1.5 text-sm text-zinc-900">
                {PIPE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            {stage1Done && !isWon && (
              <button data-testid="auto-run-btn" onClick={autoRun} disabled={deal.engine_status === "running"} className="flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-50">
                {deal.engine_status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 text-gold" />}
                {deal.engine_status === "running" ? "Anvil running…" : "Auto-run with Anvil"}
              </button>
            )}
            {stage1Done && !isWon && (
              <button data-testid="key-personnel-btn" onClick={() => setShowKP(true)} className="flex items-center gap-2 bg-white border border-zinc-300 text-zinc-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-zinc-50">
                <Users className="h-4 w-4 text-gold-dark" /> Key Personnel
              </button>
            )}
            <button data-testid="deal-request-help-btn" onClick={requestHelp}
              className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-amber-100">
              <HelpCircle className="h-4 w-4" /> Request help
            </button>
            <button data-testid="deal-request-review-btn" onClick={requestReview}
              className="flex items-center gap-2 bg-sky-50 border border-sky-300 text-sky-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-sky-100">
              <Eye className="h-4 w-4" /> Request review
            </button>
            {isOfficer && !isWon && (
              <button data-testid="mark-won-btn" onClick={markWon} className="flex items-center gap-2 bg-gold-dark text-white text-sm font-semibold px-4 py-2 rounded-md hover:opacity-90 ml-auto"><Trophy className="h-4 w-4" /> Mark Won</button>
            )}
            {isOfficer && stage1Done && !isWon && (
              <div className="w-full flex items-center gap-3 flex-wrap pt-3 mt-1 border-t border-zinc-200" data-testid="reset-controls-row">
                <ResetControl deal={deal} reload={load} />
              </div>
            )}
          </div>
        ) : null}
      </div>

      {showKP && <KeyPersonnelModal dealId={deal.id} onClose={() => setShowKP(false)} onInserted={load} />}

      {(deal.source_content_present === false || /NO SOURCE DOCUMENTS/i.test(deal.engine_error || "")) && (
        <SourceDocGate dealId={deal.id} onDone={load} />
      )}

      {deal.stage1_brief && ["advisory", "partial"].includes(deal.engine_status) && (
        <Stage1Brief deal={deal} onDone={load} />
      )}

      {deal.not_biddable === true && deal.engine_status === "no_bid" && !deal.stage1_brief && (
        <NonBiddableGate dealId={deal.id} reason={deal.not_biddable_reason} onDone={load} />
      )}

      {stage1Done && !isWon && deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed" && (
        <SendReadinessBanner dealId={deal.id} />
      )}

      {stage1Done && !isWon && deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed" && (
        <PackageTruth dealId={deal.id} />
      )}

      {deal.bid_type === "Federal" && stage1Done && !isWon && deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed" && (
        <PricingReadinessPanel dealId={deal.id} isOfficer={isOfficer} reload={load} />
      )}

      {deal.bid_type === "Federal" && stage1Done && !isWon && deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed" && (
        <PricingGroundingCard dealId={deal.id} />
      )}

      {deal.bid_type === "Federal" && stage1Done && !isWon && deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed" && (
        <ManualFormsPanel dealId={deal.id} />
      )}

      {deal.bid_type === "Federal" && stage1Done && !isWon && deal.pipeline_stage !== "Lost" && deal.pipeline_stage !== "Passed" && (
        <IntakeFormsPanel dealId={deal.id} />
      )}

      {isOfficer && stage1Done && !isWon && <FableVerify dealId={deal.id} />}

      {/* Equal 50/50 cockpit (left) + preview (right), same height, each scrolls internally */}
      <div className="grid lg:grid-cols-2 gap-5 items-stretch">
        <AnvilCockpit deal={deal} reload={load} startSignal={startSignal} onAutoRun={autoRun} canRun={stage1Done && !isWon} />
        <PreviewTabs deal={deal} reload={load} />
      </div>

      <DocumentsCard deal={deal} reload={load} />
      {deal.bid_type === "Federal" && stage1Done && (
        <div className="mt-5"><LaborBreakout deal={deal} /></div>
      )}
      {isWon && <div className="mt-5"><CrewPayroll deal={deal} users={users} reload={load} /></div>}
    </div>
  );
}
